package dev.lingvo.shared.screenshots.geom

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.TextLayer

/** Keeps panorama ordering intact while reflecting single-page layouts. */
fun mirror(page: Page, stripW: Double): Page =
    if (page.slotIds.size > 1) page else page.copy(layers = mirror(page.layers, stripW))

/** Reflects positions and decorative paint locally, preserving each capture's reading direction. */
fun mirror(layers: List<Layer>, stripW: Double): List<Layer> = layers.map { layer ->
    val box = layer.base.box
    val base = layer.base.copy(box = box.copy(x = stripW - box.x - box.w, rotation = -box.rotation))
    when (layer) {
        is BackgroundLayer -> layer.copy(base = base, fill = reflected(layer.fill), blobs = layer.blobs.map { it.copy(cx = 1 - it.cx) })
        is DeviceLayer -> layer.copy(base = base)
        is GroupLayer -> layer.copy(base = base, children = mirror(layer.children, box.w))
        is ImageLayer -> layer.copy(base = base, stroke = layer.stroke?.let { it.copy(paint = reflected(it.paint)) })
        is ShapeLayer -> layer.copy(base = base, fill = layer.fill?.let(::reflected),
            stroke = layer.stroke?.let { it.copy(paint = reflected(it.paint)) }, points = layer.points.map { it.copy(x = box.w - it.x) })
        is TextLayer -> layer.copy(base = base, style = layer.style.copy(color = reflected(layer.style.color)))
    }
}

private fun reflected(paint: Paint): Paint = when (paint) {
    is Paint.Solid -> paint
    is Paint.Linear -> paint.copy(angle = (360 - paint.angle) % 360)
    is Paint.Radial -> paint.copy(cx = 1 - paint.cx)
}

/** Replaces common layer metadata without losing subtype-specific document fields. */
internal fun Layer.withBase(base: LayerBase): Layer = when (this) {
    is BackgroundLayer -> copy(base = base)
    is DeviceLayer -> copy(base = base)
    is GroupLayer -> copy(base = base)
    is ImageLayer -> copy(base = base)
    is ShapeLayer -> copy(base = base)
    is TextLayer -> copy(base = base)
}
